import { IconButton, type IconButtonProps } from "./IconButton";
import { Close } from "@/icons";
import { cn } from "@/lib/cn";
import * as Dialog from "@radix-ui/react-dialog";
import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from "react";
import { SurfaceProvider } from "./Surface";

/** Horizontal padding every sheet and dialog part shares. */
export const SHEET_GUTTER = "px-5 md:px-6";

export type CloseButtonProps = Omit<IconButtonProps, "icon" | "label"> & { label?: string };

/** The X that dismisses a sheet, dialog or overlay: a ghost neutral circle (`variant="glass"` over photos). */
export const CloseButton = forwardRef<HTMLButtonElement, CloseButtonProps>(function CloseButton(
  { label = "Fechar", size = "icon-sm", iconSize = "md", ...rest },
  ref,
) {
  return <IconButton {...rest} ref={ref} icon={Close} label={label} size={size} iconSize={iconSize} />;
});

export type SheetHeaderProps = {
  title: ReactNode;
  description?: ReactNode;
  /** Renders a CloseButton at the end of the header. */
  onClose?: () => void;
  closeLabel?: string;
  align?: "start" | "center";
  /** Draws a hairline under the header (a sheet whose body starts with a field). */
  bordered?: boolean;
  className?: string;
};

/** Sheet/dialog heading wired to Radix `Dialog.Title` and `Dialog.Description` for the accessible name. */
export function SheetHeader({ title, description, onClose, closeLabel, align = "start", bordered = false, className }: SheetHeaderProps) {
  return (
    <div className={cn("flex items-start gap-3 pb-3 pt-2", SHEET_GUTTER, bordered && "mb-3 border-b border-border", className)}>
      <div className={cn("flex min-w-0 flex-1 flex-col gap-1", align === "center" && "items-center text-center")}>
        <Dialog.Title className="text-balance text-xl font-bold leading-tight text-card-text">
          {title}
        </Dialog.Title>
        {description ? (
          <Dialog.Description className="text-base text-card-text/75">{description}</Dialog.Description>
        ) : null}
      </div>
      {onClose ? <CloseButton label={closeLabel} onClick={onClose} className="-mr-2 -mt-1" /> : null}
    </div>
  );
}

export type SheetDescriptionProps = {
  children: ReactNode;
  align?: "start" | "center";
  className?: string;
};

/** The sheet's `Dialog.Description` rendered in the body (under a photo); pair with `BottomSheet describedByChildren`. */
export function SheetDescription({ children, align = "start", className }: SheetDescriptionProps) {
  return (
    <Dialog.Description className={cn("text-base text-card-text/75", align === "center" && "text-center", className)}>
      {children}
    </Dialog.Description>
  );
}

const BODY_HEIGHT = { none: "", md: "max-h-sheet-sm", lg: "max-h-sheet" } as const;

export type SheetBodyProps = ComponentPropsWithoutRef<"div"> & {
  /** Caps the height so the body scrolls inside the sheet. */
  maxHeight?: keyof typeof BODY_HEIGHT;
  /** `flush` drops the side gutter so a list runs full-bleed; its rows pad themselves (`ListRow inset`). */
  gutter?: "sheet" | "flush";
};

/** Scrollable content area with the shared sheet gutter. */
export const SheetBody = forwardRef<HTMLDivElement, SheetBodyProps>(function SheetBody(
  { maxHeight = "none", gutter = "sheet", className, children, ...rest },
  ref,
) {
  return (
    <div
      {...rest}
      ref={ref}
      className={cn("min-h-0 flex-1 overflow-y-auto overscroll-contain", gutter === "sheet" && SHEET_GUTTER, BODY_HEIGHT[maxHeight], className)}
    >
      <SurfaceProvider surface="card">{children}</SurfaceProvider>
    </div>
  );
});

export type SheetFooterProps = ComponentPropsWithoutRef<"div"> & {
  /** Draws a divider above the actions. */
  bordered?: boolean;
};

/** Action row pinned under the body; pads for the home indicator (safe area). */
export const SheetFooter = forwardRef<HTMLDivElement, SheetFooterProps>(function SheetFooter(
  { bordered = false, className, children, ...rest },
  ref,
) {
  return (
    <div
      {...rest}
      ref={ref}
      className={cn("flex flex-col gap-3 pb-safe pt-3", SHEET_GUTTER, bordered && "border-t", className)}
    >
      <SurfaceProvider surface="card">{children}</SurfaceProvider>
    </div>
  );
});
